<template>
  <div>
    <empty-state
      v-if="!restaurantIds"
      icon="mdi-food-apple"
      :title="$t('select_restaurant')"
      :subtitle="$t('please_select_restaurant_to_view_data')"
    />

    <empty-state
      v-else-if="isLoading || !data || data.length === 0"
      :loading="isLoading"
      :title="isLoading ? $t('loading') : $t('no_data_available')"
      :subtitle="!isLoading ? $t('no_namirnice_found') : ''"
      icon="mdi-food-apple"
    />

    <div v-else class="pa-1">
      <!-- Mobile View -->
      <mobile-data-cards
        v-if="device.isMobile"
        :headers="mobileHeaders"
        :items="customSearch(data, mobileHeaders, searchQuery)"
        :loading="isLoading"
        item-key="name"
        :fields-per-row="2"
        :show-row-number="false"
      />

      <!-- Desktop View -->
      <v-data-table
        v-else
        :headers="headers"
        :items="customSearch(data, headers, searchQuery)"
        class="striped-table ordera-box-shadow"
        :loading="isLoading"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:item.num="{ index }">
          {{ index + 1 }}
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import axios from "@/plugins/axios";

export default {
  name: "AdminNamirniceTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    EmptyState,
    MobileDataCards,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
    selectedRestaurants: Array,
    restaurantIds: String,
  },
  data() {
    return {
      data: [],
      isLoading: false,
    };
  },
  computed: {
    selectedRestaurant() {
      return this.selectedRestaurants && this.selectedRestaurants.length > 0
        ? this.selectedRestaurants[0]
        : null;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("namirnice"), value: "name", sortable: false },
        { text: this.$t("quantity"), value: "quantity", sortable: false, align: "center" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("namirnice"), value: "name", sortable: false, cols: 8 },
        { text: this.$t("quantity"), value: "quantity", sortable: false, align: "center", cols: 4 },
      ];
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printNamirniceTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportNamirniceExcel();
    },
    dateRange: {
      handler() {
        if (this.selectedRestaurant) {
          this.loadData();
        }
      },
    },
    selectedRestaurant: {
      handler() {
        if (this.selectedRestaurant) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    async loadData() {
      if (!this.selectedRestaurant || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          restaurant_id: this.selectedRestaurant.id,
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        // TODO: Replace with actual API endpoint when available
        const response = await axios.get("/admin/namirnice", { params });
        this.data = response.data.data || response.data;
      } catch (error) {
        console.error("Error loading namirnice data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    printNamirniceTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: item.name || '',
        quantity: item.quantity || 0,
      }));

      this.printDocument({
        pageTitle: this.$t('namirnice'),
        title: `${this.selectedRestaurant?.name || ""} - ${this.$t('namirnice')}`,
        subtitle: `${this.$t('date')}: ${this.dateRange[0]} - ${this.dateRange[1]}`,
        tables: [
          {
            headers: this.headers.map(h => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
          }
        ],
      });
    },
    exportNamirniceExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: item.name || '',
        quantity: item.quantity || 0,
      }));

      this.downloadExcel({
        fileName: `${this.selectedRestaurant?.name || "admin"}_namirnice_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t('namirnice'),
            title: `${this.selectedRestaurant?.name || ""} - ${this.$t('namirnice')}`,
            subtitle: `${this.$t('date')}: ${this.dateRange[0]} - ${this.dateRange[1]}`,
            headers: this.headers.map(h => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
          }
        ],
      });
    },
  },
};
</script>
